import React from 'react';
import Image from 'next/legacy/image';
import { Block, BLOCKS, INLINES, Inline } from '@contentful/rich-text-types';
import { RenderNode } from '@contentful/rich-text-react-renderer';

import { IFullwidthHero20Fields } from '../../../types/contentful';

import cx from 'classnames';
// Removed Text in favor of Tailwind typography classes
import RichText from '../RichText';

import Link from 'next/link';

import shield from '@/images/shield.svg';
import PictogramComponent from '../AtomicComponents/Pictogram';

type PrivacyPolicyDisclaimerFields = Pick<
  IFullwidthHero20Fields,
  'privacyPolicyDisclaimer' | 'displayPrivacyPolicyIcon'
>;

interface PrivacyPolicyDisclaimerProps extends PrivacyPolicyDisclaimerFields {
  noMargin?: boolean;
  color?: string;
  colorHex?: string;
  centerAlignTablet?: boolean;
  useAltLayout?: boolean;
}

export default function PrivacyPolicyDisclaimer({
  privacyPolicyDisclaimer,
  displayPrivacyPolicyIcon,
  noMargin = false,
  color = 'black',
  colorHex,
  centerAlignTablet = false,
  useAltLayout = false
}: PrivacyPolicyDisclaimerProps) {
  const RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
      return (
        <p
          className={cx(
            'm-auto w-full whitespace-pre-wrap break-words text-left text-xs tablet:m-0 [&:not(:last-child)]:mb-4',
            {
              'tablet:text-left': !centerAlignTablet,
              'tiny:text-center desktop:text-left': centerAlignTablet
            },
            colorHex ? undefined : `text-${color}`
          )}
          style={colorHex ? { color: colorHex } : undefined}
        >
          {children}
        </p>
      );
    },
    [BLOCKS.HEADING_6]: (_node: Block | Inline, children) => (
      <p
        className={`typography-body-1 tablet:typography-title-4 mb-4 font-medium ${
          displayPrivacyPolicyIcon !== false
            ? 'text-left'
            : 'text-center tablet:text-left'
        }`}
      >
        {children}
      </p>
    ),
    [INLINES.HYPERLINK]: (node, children) => {
      const href = node.data.uri as string;
      const hoverClass = colorHex
        ? colorHex !== '#1D252C'
          ? 'hover:text-[#3BBFAD]'
          : 'hover:text-[#035C67]'
        : color === 'black'
        ? 'hover:text-[#035C67]'
        : 'hover:text-[#3BBFAD]';
      return (
        <Link
          href={href}
          className={cx(
            'underline underline-offset-2 transition-colors duration-200 ease-in-out',
            hoverClass
          )}
          style={colorHex ? { color: colorHex } : undefined}
        >
          {children}
        </Link>
      );
    },
    [INLINES.EMBEDDED_ENTRY]: (node) => {
      type InlineEntryTarget = {
        data?: {
          target?: {
            sys?: { contentType?: { sys?: { id?: string } } };
            fields?: {
              url?: string;
              title?: string;
              openInNewTab?: boolean;
            };
          };
        };
      };
      const { data } = node as InlineEntryTarget;
      const target = data?.target;
      const sys = target?.sys;
      const fields = target?.fields ?? {};
      const { url, title, openInNewTab } = fields as {
        url?: string;
        title?: string;
        openInNewTab?: boolean;
      };

      const hoverClass = colorHex
        ? colorHex !== '#1D252C'
          ? 'hover:text-[#3BBFAD]'
          : 'hover:text-[#035C67]'
        : color === 'black'
        ? 'hover:text-[#035C67]'
        : 'hover:text-[#3BBFAD]';

      if (sys?.contentType?.sys?.id === 'link' && url) {
        return (
          <a
            href={url}
            target={openInNewTab ? '_blank' : undefined}
            rel={openInNewTab ? 'noopener noreferrer' : undefined}
            className={cx(
              'text-inherit underline underline-offset-2 transition-colors duration-200 ease-in-out',
              hoverClass
            )}
            style={colorHex ? { color: colorHex } : undefined}
          >
            {title}
          </a>
        );
      }
      return null;
    },
    [BLOCKS.HR]: () => <div className="mb-1 h-px w-full bg-line" />
  };

  return (
    <div className="column m-auto w-full tablet:m-0">
      <div
        className={cx('flex flex-row items-center justify-between', {
          'm-0': noMargin,
          'm-auto tablet:m-0': !noMargin
        })}
      >
        {/* If displayPrivacyPolicyIcon isn't set then we want to default to displaying the icon */}
        {!useAltLayout && displayPrivacyPolicyIcon !== false && (
          <Image src={shield} alt="" />
        )}
        {useAltLayout && displayPrivacyPolicyIcon !== false && (
          <PictogramComponent
            icon="ic_check_shield_filled"
            size="xs"
            type="primarySurface"
            shape="circle"
          />
        )}
        <div className="flex flex-col">
          <RichText
            data={privacyPolicyDisclaimer}
            overrideOptions={RICH_TEXT_OVERRIDE}
          />
        </div>
      </div>
    </div>
  );
}
